import React, { useState, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Button } from '@material-ui/core';
import {
  Circle,
  CaretRight,
  CheckCircle,
  CaretUp,
  CaretDown,
} from 'phosphor-react';
import {
  setQuickStartGuideCompleted,
  disableQuickStartGuide,
  setSearchPreferencesCompleted,
  setPreferencesStarted,
} from '../../../../actions/dashboard';
import { updateProfile } from '../../../../actions/auth';
import styles from './../../../../styles/sass/components/QuickStartGuide.module.scss';
import { DocumentBlob, SmileyBlob } from '../../../utility/Icons';
import { fetchFavorites } from '../../../../actions/favorite';
import { checkIfSurveyCompleted } from '../../../../lib/helpers';

interface Props {
  dispatch: any;
  router: any;
  feedSearch: any;
  searchPreferencesCompleted: boolean;
  firstSearchCompleted: boolean;
  firstFavoriteCompleted: boolean;
  quickStartCompleted: boolean;
  signupSurveyDetails: any;
  preferencesStarted: boolean;
  token: string;
  user: any;
  onboarding: any;
}

export const QuickStartGuide: React.FC<Props> = ({
  router,
  dispatch,
  feedSearch,
  searchPreferencesCompleted,
  firstSearchCompleted,
  firstFavoriteCompleted,
  quickStartCompleted,
  signupSurveyDetails,
  preferencesStarted,
  token,
  user,
  onboarding,
}) => {
  useEffect(() => {
    if (!onboarding.quickStartComplete && quickStartCompleted) {
      dispatch(
        updateProfile(
          { onboarding: { ...user.onboarding, quickStartComplete: true } },
          token,
          false,
          true,
        ),
      );
    }
  }, []);

  useEffect(() => {
    if (!firstFavoriteCompleted) {
      dispatch(fetchFavorites(token));
    }
    if (!searchPreferencesCompleted) {
      const surveyCompleted = checkIfSurveyCompleted(signupSurveyDetails);
      if (surveyCompleted) {
        dispatch(setSearchPreferencesCompleted());
      }
    }
  }, []);

  useEffect(() => {
    if (
      firstSearchCompleted &&
      firstFavoriteCompleted &&
      searchPreferencesCompleted
    ) {
      if (!onboarding.quickStartComplete) {
        dispatch(
          updateProfile(
            { onboarding: { ...onboarding, quickStartComplete: true } },
            token,
            false,
            true,
          ),
        );
      }
      dispatch(setQuickStartGuideCompleted());
    }

    return () => {
      if (quickStartCompleted) {
        dispatch(disableQuickStartGuide());
      }
    };
  }, [
    firstSearchCompleted,
    firstFavoriteCompleted,
    searchPreferencesCompleted,
    quickStartCompleted,
  ]);

  const [completedExpanded, expandCompleted] = useState(false);

  const navigateToSearch = useCallback(() => {
    router.push({
      pathname: '/search',
      query: {
        location: feedSearch.location,
        filters: JSON.stringify(feedSearch),
      },
    });
  }, [feedSearch]);

  const navigateToSearchPreferences = () => {
    const progress: any = checkIfSurveyCompleted(signupSurveyDetails, true);
    let nextStep = '';
    if (!progress || !progress.basicsCompleted) {
      nextStep = 'basics';
    } else if (progress.basicsCompleted && !progress.neighborhoodCompleted) {
      nextStep = 'neighborhood';
    } else {
      nextStep = 'lifestyle';
    }
    dispatch(setPreferencesStarted());
    router.push({
      pathname: '/survey',
      query: { stage: 'info', part: nextStep },
    });
  };

  const handleSearch = () => {
    navigateToSearch();
  };

  const handleExpanded = () => expandCompleted(!completedExpanded);

  const renderItems = () => {
    return (
      <div className={styles.itemsContainer}>
        <div>
          {/* Tell us about yourself */}
          <div className={styles.item}>
            <div className={styles.leftContent}>
              <div className={styles.tickSign}>
                {searchPreferencesCompleted ? (
                  <CheckCircle size={32} color="#2C5853" weight="fill" />
                ) : (
                  <CheckCircle size={32} color="#ABABAB" weight="fill" />
                )}
              </div>
              <div className={styles.itemText}>
                <div className={styles.itemMainText}>
                  Tell us about yourself
                </div>
                <div className={styles.itemSubText}>
                  In order to help you, we'll need to know what you're looking
                  for
                </div>
              </div>
            </div>
            {!searchPreferencesCompleted && (
              <div className={styles.startTaskSection}>
                <Button
                  className={styles.taskButton}
                  onClick={navigateToSearchPreferences}
                >
                  {preferencesStarted ? `Continue Task` : `Start Task`}
                  <CaretRight
                    size={32}
                    color="#443461"
                    weight="regular"
                    style={{ margin: 'auto' }}
                  />
                </Button>
              </div>
            )}
            {searchPreferencesCompleted && (
              <button className={styles.medalButtonComplete}>Completed</button>
            )}
          </div>
          <hr className={styles.simpleLine} />

          {/* Search for a home */}
          <div className={styles.item}>
            <div className={styles.leftContent}>
              <div className={styles.tickSign}>
                {firstSearchCompleted ? (
                  <CheckCircle size={32} color="#2C5853" weight="fill" />
                ) : (
                  <CheckCircle size={32} color="#ABABAB" weight="fill" />
                )}
              </div>
              <div className={styles.itemText}>
                <div className={styles.itemMainText}>Search for a home</div>
                <div className={styles.itemSubText}>
                  Start your journey by searching for homes
                </div>
              </div>
            </div>
            {!firstSearchCompleted && (
              <div className={styles.startTaskSection}>
                <Button className={styles.taskButton} onClick={handleSearch}>
                  Start Task{' '}
                  <CaretRight
                    size={32}
                    color="#443461"
                    weight="regular"
                    style={{ margin: 'auto' }}
                  />
                </Button>
              </div>
            )}
            {firstSearchCompleted && (
              <button className={styles.medalButtonComplete}>Completed</button>
            )}
          </div>
          <hr className={styles.simpleLine} />

          {/* Favorite a home */}
          <div className={styles.item}>
            <div className={styles.leftContent}>
              <div className={styles.tickSign}>
                {firstFavoriteCompleted ? (
                  <CheckCircle size={32} color="#2C5853" weight="fill" />
                ) : (
                  <CheckCircle size={32} color="#ABABAB" weight="fill" />
                )}
              </div>
              <div className={styles.itemText}>
                <div className={styles.itemMainText}>Favorite a home</div>
                <div className={styles.itemSubText}>
                  Favorite your first home
                </div>
              </div>
            </div>
            {!firstFavoriteCompleted && (
              <div className={styles.startTaskSection}>
                <button
                  className={styles.taskButton}
                  onClick={navigateToSearch}
                >
                  Start Task{' '}
                  <CaretRight
                    size={32}
                    color="#443461"
                    weight="regular"
                    style={{ margin: 'auto' }}
                  />
                </button>
              </div>
            )}
            {firstFavoriteCompleted && (
              <button className={styles.medalButtonComplete}>Completed</button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // This will be rendered if not all items are completed from the quick start guide
  const renderIncompleted = () => {
    return (
      <div className={styles.container}>
        <div className={styles.photoContainer}>
          <DocumentBlob />
        </div>
        <div>
          <div className={styles.headingText}>Your Quick Start Guide</div>
          <button className={styles.medalButton}>To-Do List</button>
          {renderItems()}
        </div>
      </div>
    );
  };

  const renderCompleted = () => {
    return (
      <div className={styles.completedContainer} style={{ marginBottom: 20 }}>
        <div className={styles.photoContainer}>
          <SmileyBlob />
        </div>
        <div className={styles.mainHeading}>
          Congrats! You’ve finished your Quick Start Guide.{' '}
          <div
            className={styles.completedTasksSideSection}
            onClick={handleExpanded}
          >
            <button className={styles.tasksButton}>View Completed Tasks</button>
            {completedExpanded ? (
              <CaretDown size={24} color="#443461" weight="fill" />
            ) : (
              <CaretUp size={24} color="#443461" weight="fill" />
            )}
          </div>
        </div>
        {completedExpanded && renderItems()}
      </div>
    );
  };

  return quickStartCompleted || onboarding.quickStartComplete
    ? renderCompleted()
    : renderIncompleted();
};

const mapStateToProps = (state: any) => ({
  token: state.auth.token,
  feedSearch: state.feed.feedSearch,
  searchPreferencesCompleted: state.dashboard.searchPreferencesCompleted,
  firstSearchCompleted: state.dashboard.firstSearchCompleted,
  firstFavoriteCompleted: state.dashboard.firstFavoriteCompleted,
  quickStartCompleted: state.dashboard.quickStartCompleted,
  preferencesStarted: state.dashboard.preferencesStarted,
  signupSurveyDetails: state.signupSurvey.signupSurveyDetails,
  user: state.auth.user,
  onboarding: state.auth.user.onboarding || {},
});

const mapDispatchToProps = (dispatch: any) => ({
  dispatch,
});

export default connect(mapStateToProps, mapDispatchToProps)(QuickStartGuide);
